import { constSelector, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { OnlineSales } from 'gw-edge-client';

import type {
    EditCoverableAttributesDTO,
    EditCoverablePriceDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import type { CoverablePriceKey, OpcCoverableAttribute } from '../pages/OpcEntryPage/types';
import { jobInfoAtom } from '../recoil/atoms/jobInfoAtom';
import { isOpcFetchingAtom } from '../recoil/atoms/isOpcFetchingAtom';
import { opcInfoAtom } from '../recoil/atoms/opcInfoAtom';
import type { ValueDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { TypeKey } from 'gw-edge-client/dist/types/dto/pc/gw/entity';
import type {
    ACCContractType_Top,
    ACCFulltimeOrLeisure_Top,
    YesNo_Top,
    DogClub_Top,
    AreaOfDestination_Top,
    MVPYearlyMileage_Top,
    InsurancePeriod,
    PrimaryHeating
} from 'gw-edge-client/dist/types/dto/pc/typekey';
import { getAccEmploymentsSelector } from '../recoil/selectors/getAccEmploymentsSelector';
import { coverableChangesAtom } from '../recoil/atoms/coverableChangesAtom';
import type { OPCAttribute } from '../recoil/types/opcTypes';
import type { PricesDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/payment';
import { set } from 'lodash';
import { jobAtom } from '../recoil/atoms/jobAtom';
import { DRAFT } from '../recoil/types/types';
import { getOPCCoverableAttributeSelector } from '../recoil/selectors/getOPCCoverableAttributeSelector';

const attributeLabelMap: Record<OPCAttribute, string> = {
    catChipId: 'Chip/ID nummer',
    dogClub: 'Hundeklub',
    dogChipId: 'Chip/ID nummer',
    prcVoluntaryAlarm: 'Frivillig alarm',
    carOdometer: 'Kilometerstand',
    traNumberInHousehold: 'Antal beboere tilmeldt folkeregisteradressen',
    traAreaOfDestination: 'Forsikringen gælder rejser i',
    adultAccEmployment: 'Beskæftigelse',
    adultAccFulltimeLeisure: 'Heltid eller Fritid?',
    adultAccContractType: 'Kontrakttype',
    carYearlyMileage: 'Kilometerinterval pr. år',
    prcGoldSilverJewelrySums: '',
    choInsurancePeriod: 'Dækningsperiode',
    prhNumOfToilets: 'Antal toiletter',
    prhPrimaryHeating: 'Opvarmning',
    prhFirePlace: 'Brændeovn/pejs',
    uvpStationary: '',
    '': ''
};

const useEditCoverablePrice = (
    coverableKey: CoverablePriceKey
): {
    editCoverablePrice?: EditCoverablePriceDTO;
    updateCoverableAttribute: (attribute: OpcCoverableAttribute) => void;
    revertChange: () => Promise<void>;
    coverableAttribute?: OpcCoverableAttribute;
} => {
    const setIsOpcFetching = useSetRecoilState(isOpcFetchingAtom);
    const [editCoverablePrice, setEditCoverablePrice] = useState<EditCoverablePriceDTO>();
    const accEmployments = useRecoilValue(getAccEmploymentsSelector);
    const currentOdometerReading = useRecoilValue(getOPCCoverableAttributeSelector)?.car
        ?.odometerReading;
    const currentOPCAttributeValue = useRecoilValue(getOPCCoverableAttributeSelector);
    const [oldAttributeCache, setOldAttributeCache] = useState<{
        attribute: string;
        value: string | TypeKey | number | boolean;
        valueDTO?: ValueDTO;
        price?: PricesDTO;
    }>();
    const [coverableAttribute, setEditCoverableAttribute] = useState<
        Omit<OpcCoverableAttribute, 'isUpdateOnly' | 'fetchOldPriceOnly'> | undefined
    >(currentOPCAttributeValue[coverableKey]);
    const [jobData, setJobData] = useRecoilState(jobAtom);

    const { jobNumber, sessionId, isPolicyChange } = useRecoilValue(jobInfoAtom);
    const [opcInfo, setOpcInfo] = useRecoilState(opcInfoAtom);
    const { attribute, isFromDAFlow } = opcInfo;

    const editCoverable: EditCoverableAttributesDTO = {
        coverableId: currentOPCAttributeValue.coverableId,
        productToChange: currentOPCAttributeValue.productToChange,
        [coverableKey]: coverableAttribute
    };

    const shoppingBasketChanges = useRecoilValue(
        isFromDAFlow ? coverableChangesAtom : constSelector(null)
    );
    const previousAttributeChangeInBasket = useMemo(() => {
        const coverableChange = shoppingBasketChanges?.find((change) => change.coverableId);
        return coverableChange?.changes.find(
            (att) => att.attribute === attributeLabelMap[attribute ?? '']
        )?.after;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initAttributeKeys = useMemo(() => {
        return Object.keys(currentOPCAttributeValue[coverableKey] ?? {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Caching the first old value (prefer value in SB if it existed, or the value retrieved from OPC call)
    useMemo(() => {
        if (!oldAttributeCache && editCoverablePrice) {
            const beforeValue = editCoverablePrice[coverableKey]?.[0]?.before;
            setOldAttributeCache({
                attribute: editCoverablePrice[coverableKey]?.[0]?.attribute ?? '',
                price: editCoverablePrice.oldPrice,
                value: getOpcValueByType(previousAttributeChangeInBasket ?? beforeValue),
                valueDTO: beforeValue
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editCoverablePrice]);

    const generateOldAttributeDTO = useCallback((): OpcCoverableAttribute => {
        const value = oldAttributeCache?.value;
        switch (attribute) {
            case 'dogClub':
                return {
                    club: value as DogClub_Top
                };
            case 'dogChipId':
            case 'catChipId':
                return {
                    chipId: value as string
                };
            case 'adultAccEmployment':
                return {
                    employmentCode: accEmployments.find((emp) => emp.name === value)?.code
                };
            case 'adultAccContractType':
                return {
                    contractType: value as ACCContractType_Top
                };
            case 'adultAccFulltimeLeisure':
                return {
                    fulltimeOrLeisure: value as ACCFulltimeOrLeisure_Top
                };
            case 'prcVoluntaryAlarm':
                return {
                    voluntaryAlarm: value as YesNo_Top
                };
            case 'traNumberInHousehold':
                return {
                    moreThanOnePersonInHousehold: value as YesNo_Top
                };
            case 'traAreaOfDestination':
                return {
                    areaOfDestination: value as AreaOfDestination_Top
                };
            case 'carOdometer':
                return {
                    odometerReading: value as string
                };
            case 'carYearlyMileage':
                return {
                    yearlyMileage: value as MVPYearlyMileage_Top,
                    odometerReading: currentOdometerReading
                };
            case 'choInsurancePeriod':
                return {
                    insurancePeriod: value as InsurancePeriod
                };
            case 'prhNumOfToilets':
                return {
                    toiletsNumber: value as number
                };
            case 'prhPrimaryHeating':
                return {
                    primaryHeating: value as PrimaryHeating
                };
            case 'prhFirePlace':
                return {
                    firePlace: value as YesNo_Top
                };
            default:
                throw new Error('Unkown attribute');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oldAttributeCache]);

    const hasEmptyAttributeValue = initAttributeKeys.some((key) => {
        const value = editCoverable[coverableKey]?.[key as keyof OpcCoverableAttribute];
        const isNumber = typeof value === 'number' && Number.isFinite(value);
        return isNumber ? false : !value;
    });

    // Auto fetch price every time edit coverable attribute shoppingBasketChanges
    useEffect(() => {
        // Fetch only old price if there is no change yet
        const isFetchingOldPrice = oldAttributeCache?.price === undefined;
        setIsOpcFetching(true);
        void OnlineSales.calculateEditPriceForOPC(jobNumber, sessionId, {
            ...editCoverable,
            fetchOldPriceOnly: isFetchingOldPrice
        })
            .then((res) => {
                if (!isPolicyChange && oldAttributeCache?.price) {
                    res.oldPrice = oldAttributeCache?.price;
                    set(res, `${coverableKey as string}[0].before`, oldAttributeCache.valueDTO);
                }
                if (res.newCoverableId) {
                    setOpcInfo({
                        ...opcInfo,
                        coverableId: res.newCoverableId
                    });
                }
                setEditCoverablePrice(res);
                if (!isFetchingOldPrice) {
                    setJobData({ ...jobData, jobStatus: DRAFT });
                }
            })
            .catch(() => {
                return undefined;
            })
            .finally(() => {
                setIsOpcFetching(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coverableAttribute]);

    const updateCoverableAttribute = useCallback(
        (attribute: OpcCoverableAttribute): void => {
            if (Object.keys(attribute).length > 1 && coverableKey !== 'car') {
                throw new Error(`Can't update more than 1 attribute`);
            }
            setEditCoverableAttribute((prevAttribute) => {
                if (prevAttribute === undefined) return attribute;
                return { ...prevAttribute, ...attribute };
            });
        },
        [coverableKey]
    );

    const revertChange = async (): Promise<void> => {
        if (hasEmptyAttributeValue) return;

        setIsOpcFetching(true);
        await OnlineSales.calculateEditPriceForOPC(jobNumber, sessionId, {
            ...editCoverable,
            [coverableKey]: generateOldAttributeDTO(),
            isUpdateOnly: false
        })
            .then((res) => {
                if (res.newCoverableId) {
                    setOpcInfo({
                        ...opcInfo,
                        coverableId: res.newCoverableId
                    });
                }
            })
            .catch(() => {
                return undefined;
            })
            .finally(() => {
                setIsOpcFetching(false);
            });
    };

    return { editCoverablePrice, updateCoverableAttribute, revertChange, coverableAttribute };
};

const getOpcValueByType = (valueDTO?: ValueDTO): string | TypeKey | number | boolean => {
    if (!valueDTO) return '';
    const { valueType, stringValue, typeKeyValue, integerValue, booleanValue } = valueDTO;
    switch (valueType) {
        case 'String':
            return stringValue ?? '';
        case 'TypeKey':
            return typeKeyValue as TypeKey;
        case 'Integer':
            return integerValue ?? 0;
        case 'Boolean':
            return booleanValue ?? false;
        default:
            throw new Error('Please check and add type rather than string and type key');
    }
};

export default useEditCoverablePrice;
